import { useWorkbenchContext } from '../../contexts/WorkbenchContext';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BankOutlined,
  CalendarOutlined,
  FormOutlined,
  RightOutlined,
  ScheduleOutlined,
  SearchOutlined,
  TableOutlined,
  TeamOutlined
} from '@ant-design/icons';
import {
  Card,
  Col,
  Empty,
  Input,
  List,
  message,
  Pagination,
  Row,
  Select,
  Spin,
  Tag,
  Typography
} from 'antd';
import { classAPI, schoolAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;
const { Option } = Select;

const MANAGEMENT_TYPES = {
  schedule: {
    title: '课程表管理',
    subtitle: '选择班级进行课程表设置',
    icon: <CalendarOutlined style={{ fontSize: 20 }} />,
    path: '/classes/schedule'
  },
  duty: {
    title: '值日生管理',
    subtitle: '选择班级设置值日生轮值',
    icon: <ScheduleOutlined style={{ fontSize: 20 }} />,
    path: '/classes/duty'
  },
  cadres: {
    title: '班干部管理',
    subtitle: '选择班级设置班干部岗位',
    icon: <TeamOutlined style={{ fontSize: 20 }} />,
    path: '/classes/cadres'
  },
  todos: {
    title: '班级待办事项',
    subtitle: '选择班级发送或管理班级待办事项',
    icon: <FormOutlined style={{ fontSize: 20 }} />,
    path: '/classes/todos'
  },
  seat: {
    title: '座位管理',
    subtitle: '选择班级管理座位布局',
    icon: <TableOutlined style={{ fontSize: 20 }} />,
    path: '/classes/seat'
  }
};

// Embedded selections remain within the school chosen on the workbench.
const DEFAULT_PAGE_SIZE = 48;

const ClassSelectPage = ({ type }) => {
  const workbench = useWorkbenchContext();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedSchool, setSelectedSchool] = useState(workbench?.schoolId || null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0
  });

  const config = MANAGEMENT_TYPES[type] || MANAGEMENT_TYPES.schedule;
  const isSuperAdmin = user && user.role === 'super_admin';

  useEffect(() => {
    if (!isSuperAdmin) return;

    const fetchSchools = async () => {
      try {
        setSchoolsLoading(true);
        const schoolRes = await schoolAPI.getAll({ pageSize: 200 });
        if (schoolRes.data.success) {
          setSchools(workbench ? schoolRes.data.data.filter(s => String(s.id) === workbench.schoolId) : schoolRes.data.data || []);
        }
      } catch (error) {
        console.error('获取学校列表失败:', error);
        message.error('获取学校列表失败');
      } finally {
        setSchoolsLoading(false);
      }
    };

    fetchSchools();
  }, [isSuperAdmin]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchClasses();
    }, 250);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, searchText, selectedSchool]);

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const classRes = await classAPI.getAll({
        page: pagination.current,
        pageSize: pagination.pageSize,
        search: searchText || undefined,
        schoolId: workbench?.schoolId || selectedSchool || undefined
      });

      if (classRes.data.success) {
        const serverPagination = classRes.data.pagination || {};
        setClasses(classRes.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: Number(serverPagination.total ?? 0)
        }));
      } else {
        message.error('获取班级列表失败');
      }
    } catch (error) {
      console.error('获取班级列表失败:', error);
      message.error('获取班级列表失败');
    } finally {
      setLoading(false);
    }
  };

  const resetToFirstPage = (patch) => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
      ...patch
    }));
  };

  const handleClassClick = (classItem) => {
    navigate(`${config.path}/${classItem.id}`);
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
          {config.icon}
          <div>
            <Title level={3} style={{ margin: 0 }}>{config.title}</Title>
            <Text type="secondary">{config.subtitle}</Text>
          </div>
        </div>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="搜索班级名称、年级、学校..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                resetToFirstPage();
              }}
              allowClear
            />
          </Col>
          {isSuperAdmin && (
            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder="选择学校筛选"
                style={{ width: '100%' }}
                value={selectedSchool}
                onChange={(value) => {
                  setSelectedSchool(value);
                  resetToFirstPage();
                }}
                allowClear
                showSearch
                loading={schoolsLoading}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {schools.map((school) => (
                  <Option key={school.id} value={school.id}>{school.name}</Option>
                ))}
              </Select>
            </Col>
          )}
        </Row>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : classes.length === 0 ? (
          <Empty description="暂无班级数据" />
        ) : (
          <>
            <List
              grid={{
                gutter: 16,
                xs: 1,
                sm: 2,
                md: 3,
                lg: 4,
                xl: 4
              }}
              dataSource={classes}
              renderItem={(item) => (
                <List.Item>
                  <Card
                    hoverable
                    onClick={() => handleClassClick(item)}
                    style={{
                      border: '1px solid #f0f0f0',
                      transition: 'all 0.3s ease'
                    }}
                    bodyStyle={{ padding: '16px' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <TeamOutlined style={{ color: '#1890ff' }} />
                          <Text strong style={{ fontSize: 16 }}>{item.name}</Text>
                        </div>

                        {item.grade && (
                          <Tag color="blue" style={{ marginBottom: 8 }}>{item.grade}</Tag>
                        )}

                        {item.school_name && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#999', fontSize: 12 }}>
                            <BankOutlined />
                            <span>{item.school_name}</span>
                          </div>
                        )}

                        {item.head_teacher_name && (
                          <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>
                            班主任: {item.head_teacher_name}
                          </div>
                        )}
                      </div>

                      <RightOutlined style={{ color: '#999', fontSize: 14 }} />
                    </div>
                  </Card>
                </List.Item>
              )}
            />
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger
                showQuickJumper
                pageSizeOptions={[24, 48, 96, 200]}
                showTotal={(total) => `共 ${total} 个班级`}
                onChange={(current, pageSize) => {
                  setPagination((prev) => ({ ...prev, current, pageSize }));
                }}
              />
            </div>
          </>
        )}
      </Card>

      <style>{`
        .ant-card-hoverable:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </div>
  );
};

export default ClassSelectPage;
